import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Checkbox, Row, Col, Alert } from 'antd';
import ProjectExportStore, { options, optionValues, ProjectExportPart } from '../stores/ProjectExportStore';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

interface Props {
    store: ProjectExportStore;
}

const ProjectExportDialog: React.FC<Props> = ({ store }) => {
    const [showMlModelsMessage, setShowMlModelsMessage] = React.useState(false);
    const [showRulesMessage, setShowRulesMessage] = React.useState(false);

    React.useEffect(() => {
        if (!store.dialogVisible) {
            setShowMlModelsMessage(false);
            setShowRulesMessage(false);
        }
    }, [store.dialogVisible]);

    const onChange = (selectedOptions: string[]) => {
        setShowMlModelsMessage(false);
        setShowRulesMessage(false);

        if (selectedOptions.includes(ProjectExportPart.Rules)) {
            if (!selectedOptions.includes(ProjectExportPart.InstructWorkflows)) {
                selectedOptions.push(ProjectExportPart.InstructWorkflows);
                setShowRulesMessage(true);
            }
            if (!selectedOptions.includes(ProjectExportPart.MlModels)) {
                selectedOptions.push(ProjectExportPart.MlModels);
                setShowRulesMessage(true);
            }
        } else if (selectedOptions.includes(ProjectExportPart.InstructWorkflows)) {
            if (!selectedOptions.includes(ProjectExportPart.MlModels)) {
                selectedOptions.push(ProjectExportPart.MlModels);
                setShowMlModelsMessage(true);
            }
        }

        store.setSelectedOptions(selectedOptions);
        store.setSelectAll(selectedOptions.length === options.length);
    };

    const onCheckAllChange = (e: CheckboxChangeEvent) => {
        setShowMlModelsMessage(false);
        setShowRulesMessage(false);
        store.setSelectedOptions(e.target.checked ? optionValues : []);
        store.setSelectAll(e.target.checked);
    };

    return (
        <Modal
            centered
            width={400}
            title="Export Project"
            destroyOnClose
            closable={false}
            maskClosable={false}
            visible={store.dialogVisible}
            className="alpha-portal-dialog"
            footer={[
                <Button
                    data-id="project-export-cancel"
                    key="project-export-cancel"
                    size="large"
                    className="light"
                    onClick={store.dispose}
                >
                    Cancel
                </Button>,
                <Button
                    data-id="project-export"
                    key="project-export"
                    size="large"
                    type="primary"
                    disabled={!store.selectedOptions.length}
                    onClick={store.exportProject}
                >
                    Export
                </Button>
            ]}
        >
            <Row style={{ marginBottom: 12 }}>
                <Col>
                    <Checkbox onChange={onCheckAllChange} checked={store.selectAll}>
                        Select all
                    </Checkbox>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Checkbox.Group
                        className="vertical-checkbox-group"
                        options={options}
                        value={store.selectedOptions}
                        onChange={onChange}
                    />
                </Col>
            </Row>

            {showMlModelsMessage && (
                <Alert
                    message="ML models are required if instruct workflows are selected"
                    type="warning"
                    closable
                    onClose={() => setShowMlModelsMessage(false)}
                    style={{ marginTop: 20 }}
                />
            )}
            {showRulesMessage && (
                <Alert
                    message="ML models and instruct workflows are required if rules are selected"
                    type="warning"
                    closable
                    onClose={() => setShowRulesMessage(false)}
                    style={{ marginTop: 20 }}
                />
            )}
        </Modal>
    );
};

export default observer(ProjectExportDialog);
